/**
 * API 주석 서비스
 * 외부 API를 사용하여 주석(하이라이트, 메모, 밑줄)을 관리합니다.
 */

import apiClient from '../../core/http/axios';
import { Annotation } from '../types/book';
import { 
  DEFAULT_HIGHLIGHT_COLOR, 
  NOTE_BORDER_COLORS, 
  getNoteColorType,
  AREA_SELECTION_BORDER,
} from '../constants/colors';


/**
 * API 주석 생성 요청 Body (API 스펙에 맞춤)
 * Path params: lecture_no, book_uid, page_no는 URL에 포함
 */
interface ApiCreateAnnotationBody {
  client_request_id: string;
  component_no: number;
  start_offset: number;
  end_offset: number;
  visibility: 'PRIVATE' | 'PUBLIC';
  text: string;  // 선택된 텍스트 내용
  type: 'HIGHLIGHT' | 'MEMO' | 'UNDERLINE' | 'BOX';
  data: {
    color?: string;
    note?: string;
    title?: string;    // MEMO 타입에서 필수
    subject?: string;  // MEMO 타입에서 필수
    [key: string]: unknown;
  };
}

/**
 * 내부에서 사용하는 주석 생성 요청 데이터
 */
export interface CreateAnnotationRequest {
  page_index: number;  // 실제 페이지 번호 (idx) - 내부 로직용
  page_no?: number;    // DB 인덱스 (page_no) - API 호출용 (없으면 page_index로 변환 시도)
  component_no: number;
  start_offset: number;
  end_offset: number;
  type: 'highlight' | 'note' | 'underline' | 'box';
  color?: string;
  note?: string;
  text?: string;  // 선택된 텍스트 내용 (API의 text 필드에 사용)
  visibility?: 'PRIVATE' | 'PUBLIC';  // 기본값: 'PRIVATE'
}

/**
 * API 주석 응답 데이터 (GET 목록 조회 시)
 */
interface ApiAnnotationData {
  annotation_uuid: string;  // UUID 형식 (annotation_id에서 변경됨)
  book_no: number;
  page_no: number;
  user_no: number;
  type: string;  // 'HIGHLIGHT' | 'MEMO' | 'UNDERLINE' | 'BOX'
  component_no: number;
  start_offset: number;
  end_offset: number;
  data: {
    color?: string;
    note?: string;
    title?: string;
    subject?: string;
    [key: string]: unknown;
  };
  is_deleted: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

// 생성/수정 응답: data.annotation 구조
interface ApiAnnotationCreateResponse {
  success: boolean;
  error: string | null;
  data: {
    seq: number;
    annotation: ApiAnnotationData;
  };
  code: string | null;
}

// 삭제 응답
interface ApiAnnotationDeleteResponse {
  success: boolean;
  error: string | null;
  data: null;
  code: string | null;
}

interface ApiAnnotationListResponse {
  success: boolean;
  error: string | null;
  data: {
    latest_seq: number;
    annotations: ApiAnnotationData[];
  };
  code: string | null;
}

interface ApiAnnotationRangeResponse {
  success: boolean;
  error: string | null;
  data: {
    book_uuid: string;
    start_page: number;
    end_page: number;
    latest_seq_map: Record<string, number>;
    annotations: Array<{
      latest_seq: number;
      annotation: ApiAnnotationData;
    }>;
  };
  code: string | null;
}

/**
 * API 응답 데이터를 내부 Annotation 타입으로 변환
 */
const convertApiAnnotationToAnnotation = (
  apiAnnotation: ApiAnnotationData
): Annotation => {
  const typeMap: Record<string, 'highlight' | 'note' | 'underline' | 'box'> = {
    'HIGHLIGHT': 'highlight',
    'MEMO': 'note',
    'UNDERLINE': 'underline',
    'BOX': 'box',
  };

  // MEMO 타입일 때 title + subject를 note로 결합
  let noteContent = apiAnnotation.data?.note || '';
  if (apiAnnotation.type === 'MEMO') {
    const title = apiAnnotation.data?.title || '';
    const subject = apiAnnotation.data?.subject || '';
    noteContent = title && subject ? `${title}\n${subject}` : (title || subject || noteContent);
  }

  return {
    id: apiAnnotation.annotation_uuid,  // annotation_id → annotation_uuid로 변경
    userId: apiAnnotation.user_no,
    bookId: apiAnnotation.book_no,
    pageNumber: apiAnnotation.page_no,
    type: typeMap[apiAnnotation.type] || 'highlight',
    color: apiAnnotation.data?.color || DEFAULT_HIGHLIGHT_COLOR,
    note: noteContent || undefined,
    componentNo: apiAnnotation.component_no, // 원본 component_no 보존
    range: {
      paragraphId: `component-${apiAnnotation.component_no}`,
      startOffset: apiAnnotation.start_offset,
      endOffset: apiAnnotation.end_offset,
      selectedText: '', // API에서 선택된 텍스트가 오지 않으므로 빈 문자열 사용
    },
    createdAt: apiAnnotation.created_at,
    updatedAt: apiAnnotation.updated_at,
  };
};

/**
 * API 필기이벤트 서비스 (메모 / 하이라이트 / 영역지정)
 */
export const annotationService = {
  /**
   * 필기이벤트 생성
   * POST /viewer/books/{book_uid}/pages/{page_no}/annotations
   * @param bookUid 책의 고유 ID
   * @param request 필기이벤트 생성 요청
   * @note API 경로의 {page_no}는 DB 인덱스(page_no)를 의미합니다. request.page_no를 사용하거나, 없으면 page_index를 사용합니다.
   */
  createAnnotation: async (
    bookUid: string,
    request: CreateAnnotationRequest
  ): Promise<Annotation> => {
    // API 경로의 {page_no}는 DB 인덱스(page_no)를 의미합니다.
    // request.page_no가 있으면 사용하고, 없으면 page_index를 사용 (하위 호환성)
    const pageNo = request.page_no ?? request.page_index;

    // type을 API가 지원하는 대문자 타입으로 변환
    const typeMap: Record<string, 'HIGHLIGHT' | 'MEMO' | 'UNDERLINE' | 'BOX'> = {
      'highlight': 'HIGHLIGHT',
      'note': 'MEMO',
      'underline': 'UNDERLINE',
      'box': 'BOX',
    };

    // UUID 생성 함수
    const generateUUID = (): string => {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      // fallback UUID v4 생성
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    };

    const apiType = typeMap[request.type] || 'HIGHLIGHT';

    // API 요청 Body 구성 (path params 제외)
    const body: ApiCreateAnnotationBody = {
      client_request_id: generateUUID(),
      component_no: request.component_no,
      start_offset: request.start_offset,
      end_offset: request.end_offset,
      visibility: request.visibility || 'PRIVATE',  // 기본값: PRIVATE
      text: request.text || '',  // 선택된 텍스트 (없으면 빈 문자열)
      type: apiType,
      data: {
        color: request.color || DEFAULT_HIGHLIGHT_COLOR, // 기본 색상
      },
    };

    // MEMO 타입일 때 title, subject, note, border_color 필수
    if (apiType === 'MEMO') {
      // note에서 첫 줄을 title로, 나머지를 subject로 분리
      const noteLines = (request.note || '').split('\n');
      body.data.title = noteLines[0] || '메모';
      body.data.subject = noteLines.slice(1).join('\n') || noteLines[0] || '메모';
      body.data.note = request.note || '';
      // color 값에 따라 border_color 설정
      const colorType = getNoteColorType(body.data.color as string);
      body.data.border_color = NOTE_BORDER_COLORS[colorType];
    } else if (apiType === 'BOX') {
      // BOX 타입일 때 border_color 필수
      body.data.border_color = AREA_SELECTION_BORDER;
    } else if (request.note) {
      body.data.note = request.note;
    }

    // console.log('[Annotation API] 생성 요청:', {
    //   url: `/viewer/books/${bookUid}/pages/${pageNo}/annotations`,
    //   body,
    //   pageNo,
    //   page_index: request.page_index,
    // });

    try {
      const response = await apiClient.post<ApiAnnotationCreateResponse>(
        `/viewer/books/${bookUid}/pages/${pageNo}/annotations`,
        body,
        {
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      // console.log('[Annotation API] 생성 응답:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.error || '필기이벤트 생성에 실패했습니다.');
      }

      // 응답 구조: data.annotation (변경됨)
      return convertApiAnnotationToAnnotation(response.data.data.annotation);
    } catch (error: any) {
      // console.error('[Annotation API] 생성 실패:', {
      //   message: error.message,
      //   response: JSON.stringify(error.response?.data, null, 2),
      //   status: error.response?.status,
      // });
      throw error;
    }
  },

  /**
   * 특정 페이지의 필기이벤트 목록 조회
   * GET /viewer/books/{book_uid}/pages/{page_no}/annotations
   * @param bookUid 책의 고유 ID
   * @param pageIndex 실제 페이지 번호(idx). DB 인덱스(page_no)가 아닙니다.
   * @note API 경로의 {page_no}는 실제 페이지 번호(idx)를 의미합니다.
   */
  getAnnotations: async (
    bookUid: string,
    pageIndex: number
  ): Promise<Annotation[]> => {
    // pageIndex는 실제 페이지 번호(idx)를 의미
    const pageIdx = pageIndex;
    // console.log('[Annotation API] 조회 요청:', { bookUid, pageIdx });

    try {
      const response = await apiClient.get<ApiAnnotationListResponse>(
        `/viewer/books/${bookUid}/pages/${pageIdx}/annotations`,
        {
          headers: {
            'accept': 'application/json',
          },
        }
      );

      // console.log('[Annotation API] 조회 응답:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.error || '필기이벤트 조회에 실패했습니다.');
      }

      // response.data.data.annotations 배열에서 삭제되지 않은 것만 필터링
      const annotationsData = response.data.data?.annotations || [];
      const annotations = annotationsData
        .filter((apiAnnotation) => !apiAnnotation.is_deleted)
        .map((apiAnnotation) => convertApiAnnotationToAnnotation(apiAnnotation));

      return annotations;
    } catch (error: any) {
      // console.error('[Annotation API] 조회 실패:', {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // });
      throw error;
    }
  },

  /**
   * 여러 페이지의 필기이벤트 목록 조회
   * GET /viewer/books/{book_uid}/annotations?start_page={start_page}&end_page={end_page}
   * @param bookUid 책의 고유 ID
   * @param startPageNo 시작 페이지 DB 인덱스(page_no)
   * @param endPageNo 끝 페이지 DB 인덱스(page_no)
   * @note start_page, end_page 파라미터는 DB 인덱스(page_no)를 의미합니다.
   */
  getAnnotationsByPageRange: async (
    bookUid: string,
    startPageNo: number,
    endPageNo: number
  ): Promise<{ annotations: Annotation[]; latestSeqMap: Record<string, number> }> => {
    // startPageNo, endPageNo는 DB 인덱스(page_no)를 의미
    // console.log('[Annotation API] 범위 조회 요청:', { bookUid, startPageNo, endPageNo });

    try {
      const response = await apiClient.get<ApiAnnotationRangeResponse>(
        `/viewer/books/${bookUid}/annotations`,
        {
          params: {
            start_page: startPageNo,
            end_page: endPageNo,
          },
          headers: {
            'accept': 'application/json',
          },
        }
      );

      // console.log('[Annotation API] 범위 조회 응답:', response.data);
      // console.log('[Annotation API] 응답 구조 확인:', {
      //   hasData: !!response.data.data,
      //   hasAnnotations: !!response.data.data?.annotations,
      //   annotationsType: Array.isArray(response.data.data?.annotations) ? 'array' : typeof response.data.data?.annotations,
      //   annotationsLength: Array.isArray(response.data.data?.annotations) ? response.data.data.annotations.length : 'N/A',
      //   rawData: response.data.data,
      // });

      if (!response.data.success) {
        throw new Error(response.data.error || '필기이벤트 범위 조회에 실패했습니다.');
      }

      // response.data.data.annotations 배열에서 삭제되지 않은 것만 필터링
      const annotationsData = response.data.data?.annotations || [];
      // console.log('[Annotation API] 원본 annotationsData:', {
      //   count: annotationsData.length,
      //   items: annotationsData.map(item => ({
      //     page_no: item.annotation?.page_no,
      //     type: item.annotation?.type,
      //     is_deleted: item.annotation?.is_deleted,
      //   })),
      // });
      
      const annotations = annotationsData
        .filter((item) => !item.annotation.is_deleted)
        .map((item) => convertApiAnnotationToAnnotation(item.annotation));

      // console.log('[Annotation API] 변환된 annotations:', {
      //   count: annotations.length,
      //   items: annotations.map(ann => ({
      //     id: ann.id,
      //     pageNumber: ann.pageNumber,
      //     type: ann.type,
      //   })),
      // });

      const latestSeqMap = response.data.data?.latest_seq_map || {};

      return {
        annotations,
        latestSeqMap,
      };
    } catch (error: any) {
      // console.error('[Annotation API] 범위 조회 실패:', {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // });
      throw error;
    }
  },

  /**
   * 필기이벤트 삭제
   * DELETE /viewer/annotations/{annotation_uuid}
   */
  deleteAnnotation: async (annotationUuid: string): Promise<void> => {
    // console.log('[Annotation API] 삭제 요청:', { annotationUuid });

    try {
      const response = await apiClient.delete<ApiAnnotationDeleteResponse>(
        `/viewer/annotations/${annotationUuid}`,
        {
          headers: {
            'accept': 'application/json',
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || '필기이벤트 삭제에 실패했습니다.');
      }
    } catch (error: any) {
      // console.error('[Annotation API] 삭제 실패:', {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // });
      throw error;
    }
  },

  /**
   * 필기이벤트 업데이트
   * PUT /viewer/annotations/{annotation_uuid}
   */
  updateAnnotation: async (
    annotation: Annotation,
    updates: { 
      note?: string; 
      color?: string;
    }
  ): Promise<Annotation> => {
    // 임시 메모(temp-로 시작)는 수정할 수 없음
    if (annotation.id.startsWith('temp-')) {
      throw new Error('임시 메모는 아직 수정할 수 없습니다. 메모 생성이 완료된 후 다시 시도해주세요.');
    }

    // console.log('[Annotation API] 업데이트 요청:', { annotationUuid: annotation.id, updates });

    // UUID 생성 함수
    const generateUUID = (): string => {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    };

    // type 변환
    const typeMap: Record<string, 'HIGHLIGHT' | 'MEMO' | 'UNDERLINE' | 'BOX'> = {
      'highlight': 'HIGHLIGHT',
      'note': 'MEMO',
      'underline': 'UNDERLINE',
      'box': 'BOX',
    };
    const apiType = typeMap[annotation.type] || 'HIGHLIGHT';

    // component_no 사용 (annotation에 저장된 값 사용)
    const componentNo = annotation.componentNo ?? 0;

    // data 객체 구성
    const colorValue = updates.color || annotation.color || DEFAULT_HIGHLIGHT_COLOR;
    const data: Record<string, unknown> = {
      color: colorValue,
    };

    // MEMO 타입일 경우 title, subject, border_color 추가
    if (apiType === 'MEMO') {
      // color 값에 따라 border_color 설정
      const colorType = getNoteColorType(colorValue);
      data.border_color = NOTE_BORDER_COLORS[colorType];
      
      if (updates.note) {
        const noteLines = updates.note.split('\n');
        data.title = noteLines[0] || '메모';
        data.subject = noteLines.slice(1).join('\n') || noteLines[0] || '메모';
      }
    }

    // API 요청 Body 구성
    const body = {
      client_request_id: generateUUID(),
      expected_version: 0,
      component_no: componentNo,
      start_offset: annotation.range.startOffset,
      end_offset: annotation.range.endOffset,
      type: apiType,
      data,
    };

    // console.log('[Annotation API] 업데이트 body:', body);

    try {
      const response = await apiClient.put<ApiAnnotationCreateResponse>(
        `/viewer/annotations/${annotation.id}`,
        body,
        {
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || '주석 업데이트에 실패했습니다.');
      }

      // 응답 구조: data.annotation (변경됨)
      return convertApiAnnotationToAnnotation(response.data.data.annotation);
    } catch (error: any) {
      // console.error('[Annotation API] 업데이트 실패:', {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // });
      throw error;
    }
  },
};
